import React, { useState } from 'react';

import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';
import TextArea from 'components/application/TextArea';

export default function UpdateRequirementPriorityModal({
  courseName,
  isRequired,
  onClose,
  onConfirmUpdateRequirementPriority,
  personnelName,
  requirementPriorityReason,
  requirementPriorityUserName,
}) {
  const [reason, setReason] = useState('');
  const { t: i18nTCommon } = useTranslation('common');
  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'training.requirementPriorityModals.UpdateRequirementPriorityModal',
  });
  const { t: i18nTShared } = useTranslation('components', {
    keyPrefix: 'training.requirementPriorityModals.shared',
  });

  const isSubmittable = reason.trim().length > 0;

  const handleConfirm = () => {
    if (isSubmittable) {
      onConfirmUpdateRequirementPriority(reason);
    }
  };

  const currentRequirementStatus =
    isRequired ?
      i18nTCommon('shared.requiredDowncase')
    : i18nTCommon('shared.optionalDowncase');
  const newRequirementStatus =
    isRequired ?
      i18nTCommon('shared.optionalDowncase')
    : i18nTCommon('shared.requiredDowncase');

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={onClose}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='mint'
            disabled={!isSubmittable}
            onClick={handleConfirm}
          >
            {i18nTCommon('shared.confirm')}
          </FilledButton>
        </div>
      }
      isOpen={true}
      modalBoxClassName='tw-w-140'
      mode='flexible'
      onClose={onClose}
    >
      <div className='tw-p-6'>
        <h2 className='tw-m-0 tw-p-0 tw-text-xl tw-font-semibold tw-text-grey-900'>
          {i18nT('title')}
        </h2>
        <p className='tw-ml-0 tw-mr-0 tw-mt-2 tw-p-0 tw-text-m tw-font-normal tw-text-grey-700'>
          {i18nT('existingRequirementPriorityReason', {
            interpolation: { escapeValue: false },
            userName: requirementPriorityUserName,
            requirementStatus: currentRequirementStatus,
            personnelName,
            courseName,
          })}
        </p>
        <div className='tw-mt-2 tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
          <p className='tw-m-0 tw-font-medium'>
            {i18nT('overrideReasonHeading')}
          </p>
          {requirementPriorityReason}
        </div>
        <hr className='tw-my-6' />
        <TextArea
          additionalLabel={i18nTCommon('shared.required')}
          label={i18nTShared('form.label')}
          name='reason'
          onChange={(e) => setReason(e.target.value)}
          placeholderText={i18nTShared('form.placeholder')}
          value={reason}
        />
        <div className='tw-mt-6 tw-flex tw-rounded-lg tw-border-0'>
          <div className='tw-mr-3 tw-h-5'>
            <InfoIcon
              className='[&_path]:tw-fill-grey-400'
              height={20}
              width={20}
            />
          </div>
          <p className='tw-m-0 tw-text-grey-600'>
            {i18nT('info', {
              newRequirementStatus,
            })}
          </p>
        </div>
      </div>
    </ModalBox>
  );
}

UpdateRequirementPriorityModal.propTypes = {
  courseName: PropTypes.string.isRequired,
  isRequired: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmUpdateRequirementPriority: PropTypes.func.isRequired,
  personnelName: PropTypes.string.isRequired,
  requirementPriorityReason: PropTypes.string.isRequired,
  requirementPriorityUserName: PropTypes.string.isRequired,
};
